import React, { FC, useEffect, useState } from "react"
import { Form, Input, Select, Modal } from "antd"
import { observer } from "mobx-react"
import { notifications } from "notifications"
import { SubmitCancelButtons } from "components"
import { useStore } from "stores/utils/use-store"
import { CountryGroupDto } from "../store/types"
import { getCountrySelectOptions, defaultFilterOption } from "../utils"

const { Option } = Select

interface CreateEditCountryGroupModalProps {
  visible: boolean
  onClose: () => void
  initialData?: CountryGroupDto | null
}

export const CreateEditCountryGroupModal: FC<CreateEditCountryGroupModalProps> = observer(({
  visible,
  onClose,
  initialData,
}) => {
  const { dataStore: { countryGroupsStore } } = useStore()
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const countryOptions = getCountrySelectOptions()

  useEffect(() => {
    if (visible) {
      if (initialData) {
        form.setFieldsValue({
          name: initialData.name,
          countries: initialData.countries,
        })
      } else {
        form.resetFields()
      }
    }
  }, [visible, initialData, form])

  const onFinishHandler = async(values: any) => {
    if (submitting) return

    const data = {
      name: values.name.trim(),
      countries: values.countries || [],
    }

    setSubmitting(true)

    try {
      if (initialData) {
        await countryGroupsStore.updateCountryGroup(initialData.id, data)
        notifications.success("Country group updated")
      } else {
        await countryGroupsStore.createCountryGroup(data)
        notifications.success("Country group created")
      }
      setSubmitting(false)
      onClose()
      form.resetFields()
    } catch (error: any) {
      notifications.error(error?.cause?.message || error?.message || "Failed to save country group")
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      open={visible}
      title={initialData ? "Edit Country Group" : "Create Country Group"}
      width="700px"
      footer={null}
      onCancel={handleClose}
      style={{ top: "10px" }}
    >
      <Form
        form={form}
        onFinish={onFinishHandler}
        layout="vertical"
        id="createEditCountryGroup"
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[
            {
              required: true,
              message: "Please enter a name",
            },
          ]}
        >
          <Input placeholder="Enter country group name" />
        </Form.Item>

        <Form.Item
          label="Countries"
          name="countries"
          rules={[
            {
              required: true,
              message: "Please select at least one country",
            },
            {
              type: "array",
              min: 1,
              message: "Please select at least one country",
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="Select countries"
            allowClear
            showSearch
            filterOption={defaultFilterOption}
            style={{ width: "100%" }}
          >
            {countryOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <SubmitCancelButtons
          formId="createEditCountryGroup"
          onClose={handleClose}
          isLoading={submitting || countryGroupsStore.isLoading}
        />
      </Form>
    </Modal>
  )
})
